import { useEffect, useState, useCallback } from 'react';
import { Building2, Award, ShieldCheck, AlertTriangle, Clock, LogIn, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { isElectron } from '../../utils/electron';
import './Admin.css';

export default function TenantInfo() {
  const { token, user, setSession, logout } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [oidc, setOidc] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Konnte Mandanteninformationen nicht laden');
      const data = await res.json();
      setInfo(data.user || data);
    } catch (err) {
      setError(err.message);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch('/api/auth/oidc/status').then(r => r.json()).then(d => setOidc(d)).catch(() => {});
  }, []);

  async function handleConnect() {
    setError(null);
    setConnecting(true);
    try {
      if (isElectron && window.electronAPI?.oidc?.connect) {
        const result = await window.electronAPI.oidc.connect();
        if (result?.token) {
          // Hydrate session with the new platform-bound token
          const meRes = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${result.token}` } });
          const me = await meRes.json();
          setSession(result.token, me.user || me);
          await load();
        }
      } else {
        // Web mode: do a hard redirect to the OIDC login route
        window.location.href = '/api/auth/oidc/login?next=' + encodeURIComponent(window.location.pathname);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setConnecting(false);
    }
  }

  function handleDisconnect() {
    logout();
  }

  if (error) {
    return <div role="alert" className="admin-error">{error}</div>;
  }
  if (!info) {
    return <div role="status" aria-live="polite" className="admin-loading">Lade Mandant…</div>;
  }

  const isPlatformLinked = Boolean(info.platform_user_id || info.platform_role || info.tenant_id);

  return (
    <div className="admin-stack">
      <header className="admin-section-header">
        <div>
          <p className="eyebrow">Mandant · Lizenz · Identität</p>
          <h1 className="admin-section-title" style={{ fontSize: '1.625rem' }}>Dein Zugang</h1>
          <p className="admin-section-desc">
            Diese Übersicht zeigt, mit welcher Identität und welchem Mandanten du angemeldet bist.
            Bei verbundenen nyxCore-Konten wird die Rolle vom OIDC-Provider gesetzt — App-seitig
            kann sie nicht überschrieben werden.
          </p>
        </div>
      </header>

      <section className="settings-section-card">
        <header className="settings-section-head">
          <h2 className="settings-section-title">Identität</h2>
        </header>
        <dl className="info-grid">
          <div>
            <dt>Benutzername</dt>
            <dd>{user?.username || info.username || '—'}</dd>
          </div>
          <div>
            <dt>E-Mail</dt>
            <dd>{info.email || '—'}</dd>
          </div>
          <div>
            <dt>Rolle (lokal)</dt>
            <dd><strong>{info.role || '—'}</strong></dd>
          </div>
          {isPlatformLinked && (
            <>
              <div>
                <dt>Mandant</dt>
                <dd>{info.tenant_slug || info.tenant_id || '—'}</dd>
              </div>
              <div>
                <dt>Plattform-Rolle</dt>
                <dd><strong>{info.platform_role || '—'}</strong></dd>
              </div>
            </>
          )}
        </dl>
      </section>

      {isPlatformLinked ? (
        <section className="settings-section-card">
          <header className="settings-section-head">
            <h2 className="settings-section-title">
              <ShieldCheck size={16} aria-hidden="true" /> nyxCore-Identität verbunden
            </h2>
            <p className="settings-section-desc">
              Du bist über den nyxCore-Identity-Provider angemeldet. Login, Rolle und Mandant
              werden zentral verwaltet — Lizenzen und Suspendierungen wirken in Echtzeit.
            </p>
          </header>
          <ul className="checklist">
            <li><Award size={14} aria-hidden="true" /> Single Sign-On via nyx-platform</li>
            <li><Clock size={14} aria-hidden="true" /> Refresh-Token-Rotation aktiv</li>
            <li><Building2 size={14} aria-hidden="true" /> Mandanten-spezifische Defaults werden automatisch geladen</li>
          </ul>
          <div className="form-actions" style={{ marginTop: 16, padding: 0 }}>
            <button type="button" className="btn btn-secondary" onClick={handleDisconnect}>
              <LogOut size={14} aria-hidden="true" />
              <span>Verbindung trennen</span>
            </button>
          </div>
        </section>
      ) : (
        <section className="settings-section-card">
          <header className="settings-section-head">
            <h2 className="settings-section-title">
              <AlertTriangle size={16} aria-hidden="true" /> Lokaler Account
            </h2>
            <p className="settings-section-desc">
              Du nutzt einen lokalen Account. Mandantenfunktionen wie Tenant-Defaults und
              Lizenzgating sind in diesem Modus deaktiviert. Wenn dein Unternehmen die nyxCore-
              Plattform nutzt, kannst du dich über &bdquo;Mit nyxCore anmelden&ldquo; verbinden.
            </p>
          </header>
          {oidc?.enabled ? (
            <div className="form-actions" style={{ marginTop: 0, padding: 0 }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConnect}
                disabled={connecting}
              >
                <LogIn size={14} aria-hidden="true" />
                <span>{connecting ? 'Verbinde…' : 'Mit nyxCore anmelden'}</span>
              </button>
              {oidc.issuer && (
                <p className="key-row-desc" style={{ margin: 0 }}>
                  Provider: <code>{oidc.issuer}</code>
                </p>
              )}
            </div>
          ) : (
            <p className="key-row-desc">
              nyxCore Identity-Plattform ist auf diesem Server nicht eingerichtet. Setze
              <code> NYXCORE_PLATFORM_URL</code> und <code>NYXCORE_OIDC_CLIENT_ID</code>, um SSO zu aktivieren.
            </p>
          )}
        </section>
      )}
    </div>
  );
}
